import type { ReactNode } from 'react';
import React, { memo } from 'react';

import type { TooltipProps } from '@mui/material';
import { Tooltip, tooltipClasses } from '@mui/material';
import { styled } from '@mui/material/styles';

type TooltipVariant = 'default' | 'secondary' | 'plain';

interface CustomTooltipProps extends Omit<TooltipProps, 'children'> {
  variant?: TooltipVariant;
  children: ReactNode;
}

const StyledTooltip = styled(
  ({ className, variant = 'default', ...props }: CustomTooltipProps & { className?: string }) => {
    // The neutral MUI tooltip — no arrow, no brand colours, default listeners/delay.
    if (variant === 'plain') {
      return <Tooltip {...(props as TooltipProps)} />;
    }

    return (
      <Tooltip
        {...(props as TooltipProps)}
        // Reduce default listeners to avoid excessive event churn across large tables
        arrow
        disableFocusListener
        disableTouchListener
        classes={{ popper: className }}
        enterDelay={150}
      />
    );
  }
)(({ theme, variant = 'default' }) =>
  variant === 'plain'
    ? {}
    : {
        [`& .${tooltipClasses.arrow}`]: {
          color: variant === 'default' ? theme.palette.gx.highlight : theme.palette.gx.response
        },
        [`& .${tooltipClasses.tooltip}`]: {
          backgroundColor:
            variant === 'default' ? theme.palette.gx.highlight : theme.palette.gx.response,
          fontSize: '12px',
          borderLeft: variant === 'secondary' ? `1px solid ${theme.palette.gx.highlight}` : 'none'
        }
      }
);

const CustomTooltip = ({ children, variant = 'default', ...props }: CustomTooltipProps) => (
  <StyledTooltip
    {...props}
    variant={variant}
  >
    {children}
  </StyledTooltip>
);

export default memo(CustomTooltip);
